/**
 * Code Import Modal Component
 * Modal for importing redeem codes from CSV file with drag-drop support
 * Displays success/error results and supports retry functionality
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useImportCodes } from '../../hooks/useRedeemGroupApi';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { ImportCodesResponse } from '../../lib/types/redeemGroup';

interface CodeImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  groupName: string;
}

export function CodeImportModal({
  isOpen,
  onClose,
  groupId,
  groupName,
}: CodeImportModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [note, setNote] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportCodesResponse | null>(
    null
  );
  const [importProgress, setImportProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importCodes = useImportCodes();
  const { reset: resetImportCodes } = importCodes;

  // Centralized state reset function
  const resetAllState = useCallback(() => {
    setSelectedFile(null);
    setNote('');
    setValidationError(null);
    setImportResult(null);
    setImportProgress(null);
    resetImportCodes();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetImportCodes]);

  // Reset state whenever modal open state changes
  useEffect(() => {
    resetAllState();
  }, [isOpen, resetAllState]);

  // Validate CSV file
  const validateFile = (file: File): string | null => {
    // Check file extension
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return 'Invalid file type. Please upload a CSV file.';
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return 'File size exceeds 10MB limit.';
    }

    return null;
  };

  // Validate CSV header
  const validateCSVHeader = async (file: File): Promise<string | null> => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const text = e.target?.result as string;
        if (!text) {
          resolve('Failed to read file contents.');
          return;
        }

        // Read first line to check header
        const firstLine = text.split('\n')[0].trim();

        // Parse CSV header by splitting on comma, trim whitespace, and strip quotes
        const parts = firstLine.split(',').map(part => {
          const trimmed = part.trim();
          // Remove surrounding quotes if present (both single and double)
          if (
            (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
            (trimmed.startsWith("'") && trimmed.endsWith("'"))
          ) {
            return trimmed.slice(1, -1);
          }
          return trimmed;
        });

        // Check if first column equals 'CODE' exactly (case-insensitive)
        if (parts[0]?.toUpperCase() !== 'CODE') {
          resolve(
            'Invalid CSV format. First row must contain a CODE column header.'
          );
          return;
        }

        resolve(null);
      };
      reader.onerror = () => {
        resolve('Failed to read file contents.');
      };
      // Read only first 1KB to check header
      reader.readAsText(file.slice(0, 1024));
    });
  };

  // Handle file selection
  const handleFileSelect = async (file: File) => {
    setValidationError(null);

    // Basic validation
    const basicError = validateFile(file);
    if (basicError) {
      setValidationError(basicError);
      return;
    }

    // Header validation
    const headerError = await validateCSVHeader(file);
    if (headerError) {
      setValidationError(headerError);
      return;
    }

    setSelectedFile(file);
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // File input change handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Upload handler
  const handleUpload = async () => {
    if (!selectedFile) return;

    // Validate note length
    if (note.length > 200) {
      setValidationError('Note cannot exceed 200 characters');
      return;
    }

    try {
      const trimmedNote = note?.trim();
      const result = await importCodes.mutateAsync({
        groupId,
        file: selectedFile,
        ...(trimmedNote && { note: trimmedNote }),
        onProgress: (current, total) => {
          setImportProgress({ current, total });
        },
      });

      if (result) {
        setImportResult(result);
        setImportProgress(null);
      }
    } catch (_error) {
      // Error is handled by the mutation
      setImportProgress(null);
    }
  };

  // Clear/retry handler
  const handleClear = () => {
    resetAllState();
  };

  const handleClose = () => {
    onClose();
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !importCodes.isPending) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, importCodes.isPending, onClose]);

  if (!isOpen) return null;

  // Handle backdrop keyboard interaction
  const handleBackdropKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!importCodes.isPending) {
        handleClose();
      }
    }
  };

  const isLoading = importCodes.isPending;
  const hasResult = !!importResult;
  const hasErrors = hasResult && importResult.errors.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          role="button"
          tabIndex={0}
          onClick={isLoading ? undefined : handleClose}
          onKeyDown={handleBackdropKeyDown}
          aria-label="Close modal by clicking backdrop"
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Import Codes
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {groupName}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          {hasResult ? (
            // Results View
            <div className="space-y-4">
              {/* Success Stats */}
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-green-600 dark:text-green-400 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-green-800 dark:text-green-300">
                      Import Completed
                    </h4>
                    <div className="mt-2 text-sm text-green-700 dark:text-green-400">
                      <p>
                        <span className="font-semibold">
                          {importResult.imported}
                        </span>{' '}
                        codes imported
                      </p>
                      {importResult.voided > 0 && (
                        <p>
                          <span className="font-semibold">
                            {importResult.voided}
                          </span>{' '}
                          codes voided
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Table */}
              {hasErrors && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Errors ({importResult.errors.length})
                  </h4>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-64">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Row
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Code
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {importResult.errors.map(error => (
                            <tr
                              key={`${error.row}-${error.code}`}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {error.row}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-gray-100">
                                {error.code}
                              </td>
                              <td className="px-4 py-3 text-sm text-red-600 dark:text-red-400">
                                {error.error}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleClear}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  Import Another File
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            // Upload View
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Upload a CSV file with a CODE column. Optional ACTION column
                  for VOID operations.
                </p>
              </div>

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
                    : selectedFile
                      ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileInputChange}
                  disabled={isLoading}
                  className="hidden"
                  aria-label="File input"
                />

                {selectedFile ? (
                  <div className="space-y-2">
                    <svg
                      className="w-12 h-12 mx-auto text-green-500 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <svg
                      className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Drag and drop your CSV file here, or
                    </p>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLoading}
                      className="text-sm font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 focus:outline-none focus:underline"
                    >
                      click to browse
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Maximum file size: 10MB
                    </p>
                  </div>
                )}
              </div>

              {/* Note Field */}
              <div>
                <label
                  htmlFor="import-note"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Note (Optional)
                </label>
                <input
                  id="import-note"
                  type="text"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  maxLength={200}
                  placeholder="e.g., Imported batch from Event X"
                  disabled={isLoading}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Optional description to help identify this batch (max 200
                  characters)
                </p>
              </div>

              {validationError ? (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {validationError}
                  </p>
                </div>
              ) : null}

              {importCodes.error ? (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {importCodes.error instanceof Error
                      ? importCodes.error.message
                      : 'An error occurred during import'}
                  </p>
                </div>
              ) : null}

              {/* Progress indicator for large imports */}
              {isLoading && importProgress && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      Processing codes...
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      {importProgress.current} / {importProgress.total}
                    </p>
                  </div>
                  <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                    <div
                      className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${(importProgress.current / importProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || isLoading || !!validationError}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Importing...
                    </>
                  ) : (
                    'Import Codes'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
